<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink, type RouteLocationRaw } from 'vue-router'
import Icon, { type IconName } from './Icon.vue'
import Spinner from './Spinner.vue'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle'
type Size = 'xs' | 'sm' | 'md' | 'lg'

const props = withDefaults(
  defineProps<{
    /** Required: an icon-only control is invisible to screen readers without it. */
    label: string
    icon?: IconName
    variant?: Variant
    size?: Size
    type?: 'button' | 'submit' | 'reset'
    disabled?: boolean
    loading?: boolean
    active?: boolean
    to?: RouteLocationRaw
    href?: string
  }>(),
  { variant: 'ghost', size: 'md', type: 'button' },
)

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-accent text-accent-ink hover:bg-accent-hover',
  secondary: 'bg-surface text-ink border border-line hover:bg-sunken',
  ghost: 'text-muted hover:bg-sunken hover:text-ink',
  danger: 'text-danger hover:bg-danger-soft',
  subtle: 'bg-accent-soft text-accent hover:brightness-95',
}

const SIZES: Record<Size, string> = { xs: 'size-6', sm: 'size-7', md: 'size-8', lg: 'size-9' }
const ICON_SIZE: Record<Size, number> = { xs: 13, sm: 14, md: 16, lg: 18 }

const classes = computed(() => [
  'inline-grid place-items-center rounded-md transition-colors duration-100',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
  'disabled:pointer-events-none disabled:opacity-45',
  VARIANTS[props.variant],
  SIZES[props.size],
  props.active ? 'bg-sunken text-ink' : '',
])
</script>

<template>
  <component
    :is="to ? RouterLink : href ? 'a' : 'button'"
    :to="to"
    :href="href"
    :type="to || href ? undefined : type"
    :disabled="to || href ? undefined : disabled || loading"
    :aria-label="label"
    :title="label"
    :aria-pressed="active"
    :class="classes"
  >
    <Spinner
      v-if="loading"
      :size="ICON_SIZE[size]"
    />
    <slot v-else>
      <Icon
        v-if="icon"
        :name="icon"
        :size="ICON_SIZE[size]"
      />
    </slot>
  </component>
</template>
